import React, { useRef, useState, useEffect } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { setupMultilingualPDF, applyFontForText, autoTableMultilingualHook } from '../../utils/pdfMultilingual';
import { hasIndicScript, exportInvoiceHtmlPdf, loadImageForPdf } from '../../utils/pdfHtmlRender';
import QRCode from 'qrcode';

// Number to words conversion for Indian currency
const numberToWords = (num) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  
  if (num === 0) return 'Zero';
  
  const convertLessThanThousand = (n) => {
    if (n === 0) return '';
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convertLessThanThousand(n % 100) : '');
  };
  
  const convertNumber = (n) => {
    if (n >= 10000000) return convertNumber(Math.floor(n / 10000000)) + ' Crore ' + convertNumber(n % 10000000);
    if (n >= 100000) return convertNumber(Math.floor(n / 100000)) + ' Lakh ' + convertNumber(n % 100000);
    if (n >= 1000) return convertNumber(Math.floor(n / 1000)) + ' Thousand ' + convertNumber(n % 1000);
    return convertLessThanThousand(n);
  };
  
  const rupees = Math.floor(num);
  const paise = Math.round((num - rupees) * 100);
  
  let result = convertNumber(rupees) + ' Rupees';
  if (paise > 0) {
    result += ' and ' + convertNumber(paise) + ' Paise';
  }
  return result + ' Only';
};

// Professional 80mm thermal printer bill component
const ThermalBillPrint = ({ bill, businessInfo, billSettings, onClose, autoPrint = false }) => {
  const printRef = useRef(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [customerDetails, setCustomerDetails] = useState(null);
  const autoPrintDone = useRef(false);

  // Auto-print on mount if autoPrint is enabled — skip preview entirely
  useEffect(() => {
    if (autoPrint && !autoPrintDone.current && printRef.current) {
      autoPrintDone.current = true;
      // Small delay to let bill content render, then print and auto-close
      setTimeout(() => {
        handleSilentPrint();
      }, 600);
    }
  }, [autoPrint]);

  // Fetch customer details for A4 invoice (address, email, GST)
  useEffect(() => {
    const fetchCustomer = async () => {
      if (bill?.customer_id && bill.customer_id !== 'guest') {
        try {
          const token = localStorage.getItem('pos_token');
          const API = process.env.REACT_APP_BACKEND_URL;
          const resp = await fetch(`${API}/api/pos/customers`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (resp.ok) {
            const customers = await resp.json();
            const cust = customers.find(c => c.id === bill.customer_id);
            if (cust) setCustomerDetails(cust);
          }
        } catch (e) { /* ignore */ }
      }
    };
    fetchCustomer();
  }, [bill?.customer_id]);

  const formatCurrency = (amount) => `₹${(amount || 0).toFixed(2)}`;
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const getPrintStyles = () => `
    @page { 
      size: 100mm auto; 
      margin: 10mm; 
    }
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
      color: #000000 !important;
    }
    body { 
      font-family: 'Courier New', 'Lucida Console', monospace; 
      font-size: 12px; 
      font-weight: bold;
      width: 80mm; 
      margin: 0 auto;
      padding: 5mm;
      line-height: 1.4;
      color: #000000;
      background: #fff;
    }
    .center { text-align: center; }
    .right { text-align: right; }
    .left { text-align: left; }
    .bold { font-weight: bold; }
    
    /* Lines and separators */
    .line-single { 
      border-top: 1px solid #000; 
      margin: 4px 0; 
    }
    .line-dashed { 
      border-top: 1px dashed #000; 
      margin: 4px 0; 
    }
    .line-double { 
      border-top: 3px double #000; 
      margin: 6px 0; 
    }
    .line-thick {
      border-top: 2px solid #000;
      margin: 4px 0;
    }
    .line-equals {
      border-top: 1px solid #000;
      border-bottom: 1px solid #000;
      height: 3px;
      margin: 6px 0;
    }
    
    /* Header styles */
    .header-business { 
      font-size: 16px; 
      font-weight: 900; 
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 2px;
    }
    .header-address { 
      font-size: 11px; 
      color: #000;
      font-weight: bold;
      line-height: 1.3;
    }
    .header-contact {
      font-size: 11px;
      font-weight: bold;
      margin-top: 2px;
    }
    .header-gst {
      font-size: 11px;
      font-weight: 900;
    }
    
    /* Document type */
    .doc-type { 
      font-size: 13px;
      font-weight: 900;
      letter-spacing: 1px;
      margin: 4px 0;
      padding: 3px 0;
      border-top: 2px solid #000;
      border-bottom: 2px solid #000;
    }
    
    /* Info section */
    .info-row {
      display: flex;
      justify-content: space-between;
      font-size: 11px;
      font-weight: bold;
      margin: 1px 0;
    }
    .info-label {
      color: #000;
    }
    .info-value {
      font-weight: 900;
    }
    
    /* Customer section */
    .customer-section {
      padding: 3px 0;
    }
    .customer-label {
      font-size: 10px;
      color: #000;
      font-weight: bold;
    }
    .customer-name {
      font-weight: 900;
      font-size: 12px;
    }
    .customer-mobile {
      font-size: 11px;
      font-weight: bold;
    }
    
    /* Items table */
    table { 
      width: 100%; 
      border-collapse: collapse; 
    }
    .items-header {
      font-weight: 900;
      font-size: 11px;
      border-bottom: 2px solid #000;
    }
    .items-header td {
      padding: 3px 1px;
    }
    .item-row td {
      padding: 3px 1px;
      vertical-align: top;
      font-size: 11px;
      font-weight: bold;
    }
    .item-name { 
      max-width: 120px; 
      word-wrap: break-word;
      font-weight: 900;
    }
    .item-hsn {
      font-size: 9px;
      color: #000;
      font-weight: bold;
    }
    .col-sno { width: 15px; }
    .col-qty { text-align: center; width: 30px; }
    .col-rate { text-align: right; width: 40px; }
    .col-amt { text-align: right; width: 50px; font-weight: 900; }
    
    /* Totals section */
    .totals-section {
      margin-top: 4px;
    }
    .total-row {
      display: flex;
      justify-content: space-between;
      padding: 2px 0;
      font-size: 11px;
      font-weight: bold;
    }
    .total-row .label {
      color: #000;
    }
    .total-row .value {
      font-weight: 900;
    }
    .total-row.discount {
      color: #000;
    }
    .total-row.grand {
      font-size: 14px;
      font-weight: 900;
      padding: 4px 0;
      margin-top: 2px;
      border-top: 2px solid #000;
      border-bottom: 2px solid #000;
    }
    .total-row.savings {
      font-size: 10px;
      color: #000;
      font-weight: bold;
      font-style: italic;
    }
    
    /* Tax summary */
    .tax-summary {
      margin-top: 4px;
      padding: 3px;
      border: 2px solid #000;
    }
    .tax-summary-title {
      font-weight: 900;
      font-size: 10px;
      text-align: center;
      margin-bottom: 2px;
    }
    .tax-table {
      font-size: 10px;
      font-weight: bold;
    }
    .tax-table td {
      padding: 1px 2px;
    }
    .tax-table thead td {
      font-weight: 900;
      border-bottom: 1px solid #000;
    }
    
    /* Payment section */
    .payment-section {
      margin-top: 4px;
      padding-top: 4px;
    }
    .payment-title {
      font-weight: 900;
      font-size: 11px;
      margin-bottom: 2px;
    }
    .payment-row {
      display: flex;
      justify-content: space-between;
      font-size: 11px;
      font-weight: bold;
      padding: 1px 0;
    }
    .payment-mode {
      text-transform: uppercase;
      font-weight: 900;
    }
    .change-row {
      font-weight: 900;
    }
    
    /* Footer */
    .footer {
      margin-top: 8px;
      text-align: center;
      padding-top: 4px;
      border-top: 1px dashed #000;
    }
    .footer-thanks {
      font-size: 12px;
      font-weight: 900;
      margin-bottom: 2px;
    }
    .footer-terms {
      font-size: 9px;
      color: #000;
      font-weight: bold;
      line-height: 1.3;
    }
    .footer-powered {
      font-size: 9px;
      color: #000;
      font-weight: bold;
      margin-top: 4px;
    }
    
    /* Notes */
    .notes-section {
      margin-top: 4px;
      padding: 3px;
      border: 2px dashed #000;
      font-size: 11px;
      font-weight: bold;
    }
    .notes-label {
      font-weight: 900;
      font-size: 10px;
    }
  `;

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank', 'width=320,height=700');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${bill.is_estimation ? 'Estimation' : 'Invoice'} - ${bill.bill_number || bill.estimation_number}</title>
          <style>${getPrintStyles()}</style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 300);
  };

  // Silent print: sends to printer and auto-closes the component (no preview shown)
  const handleSilentPrint = () => {
    const printContent = printRef.current;
    if (!printContent) { onClose(); return; }
    const printWindow = window.open('', '_blank', 'width=320,height=700');
    if (!printWindow) { onClose(); return; }
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${bill.is_estimation ? 'Estimation' : 'Invoice'} - ${bill.bill_number || bill.estimation_number}</title>
          <style>${getPrintStyles()}</style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
      // Auto-close the preview component after printing
      if (onClose) onClose();
    }, 400);
  };

  const handleDownloadPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const fileName = `${bill.is_estimation ? 'EST' : 'INV'}_${bill.bill_number || bill.estimation_number}`;
      
      // Create a temporary container with the bill content
      const printContent = printRef.current;
      
      // Use html2canvas to capture the bill with higher scale for better quality
      const canvas = await html2canvas(printContent, {
        scale: 3,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      // Calculate dimensions for PDF with margins
      const marginLeft = 10;
      const marginRight = 10;
      const marginTop = 10;
      const marginBottom = 10;
      const contentWidth = 80; // Content width in mm
      const pageWidth = contentWidth + marginLeft + marginRight;
      const imgHeight = (canvas.height * contentWidth) / canvas.width;
      const pageHeight = imgHeight + marginTop + marginBottom;
      
      // Create PDF with margins
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [pageWidth, pageHeight]
      });
      
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', marginLeft, marginTop, contentWidth, imgHeight);
      
      // Download PDF
      pdf.save(`${fileName}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      // Fallback to print dialog
      const printContent = printRef.current;
      const printWindow = window.open('', '_blank', 'width=320,height=700');
      
      const fileName = `${bill.is_estimation ? 'EST' : 'INV'}_${bill.bill_number || bill.estimation_number}`;
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${fileName}</title>
            <style>${getPrintStyles()}</style>
          </head>
          <body>
            ${printContent.innerHTML}
            <script>
              window.onload = function() {
                window.print();
              };
            <\/script>
          </body>
        </html>
      `);
      
      printWindow.document.close();
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleShare = async () => {
    setIsGeneratingPDF(true);
    try {
      const fileName = `${bill.is_estimation ? 'Estimation' : 'Invoice'}_${bill.bill_number || bill.estimation_number}`;
      
      // Generate PDF blob
      const printContent = printRef.current;
      const canvas = await html2canvas(printContent, {
        scale: 3, // Higher scale for better quality
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      // Add margins: 10mm on each side
      const marginLeft = 10;
      const marginRight = 10;
      const marginTop = 10;
      const marginBottom = 10;
      const contentWidth = 80; // Content width in mm
      const pageWidth = contentWidth + marginLeft + marginRight; // Total page width
      const imgHeight = (canvas.height * contentWidth) / canvas.width;
      const pageHeight = imgHeight + marginTop + marginBottom;
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [pageWidth, pageHeight]
      });
      
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', marginLeft, marginTop, contentWidth, imgHeight);
      
      const pdfBlob = pdf.output('blob');
      const pdfFile = new File([pdfBlob], `${fileName}.pdf`, { type: 'application/pdf' });
      
      // Check if Web Share API is available and can share files
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
        await navigator.share({
          title: `${bill.is_estimation ? 'Estimation' : 'Invoice'} - ${businessInfo?.business_name || 'GPOS'}`,
          text: `${bill.is_estimation ? 'Estimation' : 'Invoice'} #${bill.bill_number || bill.estimation_number}\nTotal: ₹${(bill.total || bill.grand_total || 0).toFixed(2)}`,
          files: [pdfFile]
        });
      } else if (navigator.share) {
        // Fallback: Share without file (text only)
        await navigator.share({
          title: `${bill.is_estimation ? 'Estimation' : 'Invoice'} - ${businessInfo?.business_name || 'GPOS'}`,
          text: generateShareText()
        });
      } else {
        // Fallback: Download PDF and show message
        pdf.save(`${fileName}.pdf`);
        alert('PDF downloaded! You can share it manually from your device.');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error sharing:', error);
        // Fallback to WhatsApp sharing
        handleShareWhatsApp();
      }
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleShareWhatsApp = () => {
    const text = generateShareText();
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const handleShareTelegram = () => {
    const text = generateShareText();
    const url = `https://t.me/share/url?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const handleShareEmail = () => {
    const subject = `${bill.is_estimation ? 'Estimation' : 'Invoice'} from ${businessInfo?.business_name || 'GPOS'} - ${bill.bill_number || bill.estimation_number}`;
    const body = generateShareText();
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const generateShareText = () => {
    const docType = bill.is_estimation ? 'ESTIMATION' : documentTitle;
    const docNum = bill.bill_number || bill.estimation_number;
    
    let text = `═══════════════════════\n`;
    text += `       ${docType}\n`;
    text += `═══════════════════════\n\n`;
    text += `${businessInfo?.business_name || 'Store'}\n`;
    text += `${getFullAddress()}\n`;
    if (businessInfo?.mobile) text += `Ph: ${businessInfo.mobile}\n`;
    if (businessInfo?.gst_number) text += `GSTIN: ${businessInfo.gst_number}\n`;
    text += `\n───────────────────────\n`;
    text += `${bill.is_estimation ? 'Est.' : 'Bill'} No: ${docNum}\n`;
    text += `Date: ${formatDate(bill.created_at)} ${formatTime(bill.created_at)}\n`;
    
    if (bill.customer_name && bill.customer_name !== 'Guest' && !bill.customer_name.includes('Guest')) {
      text += `Customer: ${bill.customer_name}\n`;
      if (bill.customer_mobile) text += `Mobile: ${bill.customer_mobile}\n`;
    }
    
    text += `───────────────────────\n\n`;
    text += `ITEMS:\n`;
    
    bill.items?.forEach((item, idx) => {
      text += `${idx + 1}. ${item.product_name}\n`;
      if (item.description) text += `   ${item.description}\n`;
      if (item.serial_number_values?.length > 0) text += `   SN: ${item.serial_number_values.join(', ')}\n`;
      if (item.hsn_code) text += `   HSN: ${item.hsn_code}`;
      if (item.mrp && billSettings?.show_mrp !== false) text += ` | MRP: ${formatCurrency(item.mrp)}`;
      if (item.hsn_code || item.mrp) text += '\n';
      text += `   ${item.quantity}${item.unit ? ' ' + item.unit : ''}${item.stock_quantity != null ? ` (${item.stock_quantity} ${item.stock_unit || 'pcs'})` : ''} x ${formatCurrency(item.price)} = ${formatCurrency(item.total)}\n`;
    });
    
    text += `\n───────────────────────\n`;
    text += `Subtotal: ${formatCurrency(bill.subtotal)}\n`;
    if (bill.discount_amount > 0) {
      text += `Discount: -${formatCurrency(bill.discount_amount)}\n`;
    }
    if (bill.tax_amount > 0) {
      const taxLabel = billSettings?.tax_method === 'inclusive' ? 'Tax (GST Incl.)' : 'Tax (GST)';
      text += `${taxLabel}: ${formatCurrency(bill.tax_amount)}\n`;
    }
    text += `═══════════════════════\n`;
    text += `TOTAL: ${formatCurrency(bill.total || bill.grand_total)}\n`;
    text += `═══════════════════════\n\n`;
    
    if (!bill.is_estimation && bill.payment_modes?.length > 0) {
      text += `Payment:\n`;
      bill.payment_modes.forEach(pm => {
        text += `  ${pm.mode.toUpperCase()}: ${formatCurrency(pm.amount)}\n`;
      });
      if (bill.change_amount > 0) {
        text += `  Change: ${formatCurrency(bill.change_amount)}\n`;
      }
      text += `\n`;
    }
    
    text += `${billSettings?.footer_text || 'Thank you for your business!'}\n`;
    
    return text;
  };

  // Calculate tax summary for GST breakdown
  const getTaxSummary = () => {
    const taxMap = {};
    bill.items?.forEach(item => {
      if (item.tax_percent > 0) {
        const key = `${item.tax_percent}`;
        if (!taxMap[key]) {
          taxMap[key] = { rate: item.tax_percent, taxable: 0, cgst: 0, sgst: 0, total: 0 };
        }
        const taxableAmount = item.total / (1 + item.tax_percent / 100);
        const taxAmount = item.total - taxableAmount;
        taxMap[key].taxable += taxableAmount;
        taxMap[key].cgst += taxAmount / 2;
        taxMap[key].sgst += taxAmount / 2;
        taxMap[key].total += taxAmount;
      }
    });
    return Object.values(taxMap);
  };

  const taxSummary = getTaxSummary();
  const totalItems = bill.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  // Get the bill title based on settings
  const getBillTitle = () => {
    if (bill.is_estimation) return 'ESTIMATION';
    
    const titleMap = {
      'tax_invoice': 'TAX INVOICE',
      'receipt': 'RECEIPT',
      'cash_bill': 'CASH BILL',
      'tax_receipt': 'TAX RECEIPT',
      'tax_bill': 'TAX BILL',
      'custom': billSettings?.bill_title_custom || 'INVOICE'
    };
    
    return titleMap[billSettings?.bill_title] || 'TAX INVOICE';
  };

  const documentTitle = getBillTitle();

  // Get full business address with city, state, postal
  const getFullAddress = () => {
    const parts = [];
    if (businessInfo?.address) parts.push(businessInfo.address);
    if (businessInfo?.city) parts.push(businessInfo.city);
    if (businessInfo?.district && businessInfo?.district !== businessInfo?.city) parts.push(businessInfo.district);
    if (businessInfo?.state) parts.push(businessInfo.state);
    if (businessInfo?.postal_code) parts.push(`- ${businessInfo.postal_code}`);
    return parts.join(', ');
  };

  // Check if customer should be shown
  const showCustomer = bill.customer_name && 
    bill.customer_name !== 'Guest' && 
    bill.customer_name !== 'Walk-in Customer' &&
    !bill.customer_name.toLowerCase().includes('guest');

  // Generate A4 GST Invoice PDF - Clean B&W professional format
  const handleGenerateA4Invoice = async () => {
    setIsGeneratingPDF(true);
    try {
      // Indic scripts (Tamil etc.) cannot be shaped by jsPDF → render via HTML capture instead.
      const indicSample = [
        businessInfo?.business_name, businessInfo?.address,
        bill?.customer_name, bill?.customer_address, bill?.notes,
        ...(bill?.items || []).flatMap(it => [it.product_name, it.description])
      ].filter(Boolean).join(' ');
      if (hasIndicScript(indicSample)) {
        await exportInvoiceHtmlPdf({ bill, businessInfo, billSettings, customerDetails });
        return;
      }
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 12;
      const contentWidth = pageWidth - (margin * 2);
      let y = margin;
      
      const isEstimation = bill?.is_estimation || bill?.status === 'estimation';
      const taxMethod = billSettings?.tax_method || 'inclusive';

      // Pre-load multilingual fonts for any Indic text in this bill
      try {
        await setupMultilingualPDF(pdf, [
          businessInfo?.business_name, businessInfo?.address,
          bill?.customer_name, bill?.customer_address, bill?.notes,
          ...(bill?.items || []).flatMap(it => [it.product_name, it.description])
        ]);
      } catch (e) { console.warn('Multilingual font load failed:', e); }
      
      // Merge customer details from bill + fetched customer record
      const custName = bill.customer_name || 'Walk-in Customer';
      const custMobile = bill.customer_mobile || customerDetails?.mobile || '';
      const custEmail = bill.customer_email || customerDetails?.email || '';
      const custAddress = bill.customer_address || customerDetails?.address || '';
      const custGST = bill.customer_gst_number || customerDetails?.gst_number || '';
      
      // Colors - minimal B&W
      const black = [0, 0, 0];
      const darkGray = [51, 51, 51];
      const gray = [120, 120, 120];
      const lineGray = [180, 180, 180];
      
      // ---- HEADER SECTION ----
      const noHeader = billSettings?.a4_no_header || false;
      const topMargin = noHeader ? (parseInt(billSettings?.a4_top_margin) || 0) : 0;
      
      if (noHeader) {
        // Letterpad mode - skip header, add top margin
        y = margin + topMargin;
        // Just show invoice title and number
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(14);
        pdf.setTextColor(...black);
        pdf.text(isEstimation ? 'ESTIMATION' : 'GST INVOICE', pageWidth / 2, y, { align: 'center' });
        y += 8;
        pdf.setFontSize(10);
        pdf.text(`${isEstimation ? 'Est' : 'Invoice'} No: ${bill.bill_number}`, margin, y);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Date: ${formatDate(bill.created_at)}`, pageWidth - margin, y, { align: 'right' });
        y += 8;
      } else {
      let logoWidth = 0;
      if (businessInfo?.logo) {
        try {
          // Re-encode via canvas so palette/transparent PNG logos render correctly
          const logoImg = await loadImageForPdf(businessInfo.logo);
          if (logoImg?.dataUrl) {
            const box = 23;
            const ratio = logoImg.width / logoImg.height || 1;
            let w = box, h = box / ratio;
            if (h > box) { h = box; w = box * ratio; }
            pdf.addImage(logoImg.dataUrl, 'PNG', margin, y, w, h);
            logoWidth = 29;
          }
        } catch (e) { logoWidth = 0; }
      }
      
      // Company Name
      if (billSettings?.a4_show_company_name !== false) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(16);
        pdf.setTextColor(...black);
        pdf.text(businessInfo?.business_name || 'Business Name', margin + logoWidth, y + 6);
      }
      
      // Company Address - wrapped properly
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(...darkGray);
      const maxAddrWidth = contentWidth * 0.55 - logoWidth;
      let addrY = y + 12;
      
      if (billSettings?.a4_show_address !== false) {
        const addrLine1 = [businessInfo?.address, businessInfo?.city].filter(Boolean).join(', ');
        const addrLine2 = [businessInfo?.district, businessInfo?.state, businessInfo?.postal_code ? `- ${businessInfo.postal_code}` : ''].filter(Boolean).join(', ');
        if (addrLine1) {
          const wrappedAddr1 = pdf.splitTextToSize(addrLine1, maxAddrWidth);
          pdf.text(wrappedAddr1, margin + logoWidth, addrY);
          addrY += wrappedAddr1.length * 4;
        }
        if (addrLine2) {
          const wrappedAddr2 = pdf.splitTextToSize(addrLine2, maxAddrWidth);
          pdf.text(wrappedAddr2, margin + logoWidth, addrY);
          addrY += wrappedAddr2.length * 4;
        }
      }
      
      // Phone & Email
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      const contactParts = [];
      if (billSettings?.a4_show_phone !== false && (businessInfo?.mobile_number || businessInfo?.mobile)) contactParts.push(businessInfo.mobile_number || businessInfo.mobile);
      if (billSettings?.a4_show_email !== false && businessInfo?.email) contactParts.push(businessInfo.email);
      if (contactParts.length > 0) { pdf.text(contactParts.join(' | '), margin + logoWidth, addrY); addrY += 4.5; }
      
      // GSTIN
      if (billSettings?.a4_show_gst_number !== false && businessInfo?.gst_number) {
        pdf.text(`GSTIN: ${businessInfo.gst_number}`, margin + logoWidth, addrY);
        addrY += 4.5;
      }
      
      // Right side: Invoice title + details
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.setTextColor(...black);
      pdf.text(isEstimation ? 'ESTIMATION' : 'GST INVOICE', pageWidth - margin, y + 6, { align: 'right' });
      
      pdf.setFontSize(10);
      const detailsBlockWidth = 55;
      const detailsX = pageWidth - margin - detailsBlockWidth;
      let dY = y + 14;
      const drawDetail = (label, value) => {
        pdf.setFont('helvetica', 'bold');
        pdf.text(label, detailsX, dY);
        const labelW = pdf.getTextWidth(label);
        pdf.setFont('helvetica', 'normal');
        const v = String(value || '');
        const valueW = pdf.getTextWidth(v);
        // If the value fits next to the label, keep it on the same line (right-aligned).
        // Otherwise wrap it onto the following line(s) so it never overlaps the label.
        if (valueW <= detailsBlockWidth - labelW - 3) {
          pdf.text(v, pageWidth - margin, dY, { align: 'right' });
          dY += 5.5;
        } else {
          dY += 5;
          const lines = pdf.splitTextToSize(v, detailsBlockWidth);
          lines.forEach((line) => {
            pdf.text(line, pageWidth - margin, dY, { align: 'right' });
            dY += 5;
          });
          dY += 0.5;
        }
      };
      drawDetail(`${isEstimation ? 'Est' : 'Invoice'} No:`, bill.bill_number);
      drawDetail('Date:', formatDate(bill.created_at));
      if (!isEstimation) {
        const payMethod = bill.payment_modes?.map(p => p.mode).join(', ') || 'Cash';
        drawDetail('Payment:', payMethod.charAt(0).toUpperCase() + payMethod.slice(1));
      }
      
      y = Math.max(addrY + 4, dY + 2);
      
      // Separator
      pdf.setDrawColor(...lineGray);
      pdf.setLineWidth(0.5);
      pdf.line(margin, y, pageWidth - margin, y);
      y += 5;
      } // end else (non-letterpad mode)
      
      // ---- BILL TO / SHIP TO ----
      const halfWidth = (contentWidth - 6) / 2;
      
      const drawCustomerBox = (startX, title) => {
        pdf.setDrawColor(...lineGray);
        pdf.setLineWidth(0.3);
        
        const maxTextW = halfWidth - 6;
        
        // Collect customer lines, wrapping long text
        const lines = [];
        lines.push({ text: custName, bold: true, size: 10 });
        if (custAddress) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(9);
          const wrappedAddr = pdf.splitTextToSize(custAddress, maxTextW);
          wrappedAddr.forEach(addrLine => {
            lines.push({ text: addrLine, bold: false, size: 9 });
          });
        }
        if (custMobile) lines.push({ text: `Ph: ${custMobile}`, bold: false, size: 9 });
        if (custEmail) lines.push({ text: `Email: ${custEmail}`, bold: false, size: 9 });
        if (custGST) lines.push({ text: `GST: ${custGST}`, bold: true, size: 9 });
        
        const boxH = 8 + (lines.length * 5.5) + 2;
        pdf.rect(startX, y, halfWidth, boxH);
        
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.setTextColor(...black);
        pdf.text(title, startX + 3, y + 5.5);
        
        let lineY = y + 11;
        lines.forEach(line => {
          pdf.setFont('helvetica', line.bold ? 'bold' : 'normal');
          pdf.setFontSize(line.size);
          pdf.setTextColor(...darkGray);
          pdf.text(line.text, startX + 3, lineY);
          lineY += 5.5;
        });
        
        return boxH;
      };
      
      const boxH1 = drawCustomerBox(margin, 'Bill To:');
      drawCustomerBox(margin + halfWidth + 6, 'Ship To:');
      y += boxH1 + 4;
      
      // ---- ITEMS TABLE (with Discount column) ----
      const items = bill.items || [];
      
      const tableData = items.map((item, index) => {
        const baseAmount = item.quantity * item.price;
        const itemDiscount = item.item_discount || 0;
        const afterDiscount = baseAmount - itemDiscount;
        const discountPercent = baseAmount > 0 ? ((itemDiscount / baseAmount) * 100) : 0;
        
        let taxableAmount;
        if (taxMethod === 'inclusive') {
          const taxDivisor = 1 + ((item.tax_percent || 0) / 100);
          taxableAmount = afterDiscount / taxDivisor;
        } else {
          taxableAmount = afterDiscount;
        }
        
        const descParts = [item.product_name];
        // Second line: description, Serial numbers, HSN code, MRP
        const detailParts = [];
        if (item.description) detailParts.push(item.description);
        if (item.serial_number_values?.length > 0) detailParts.push(`S/N: ${item.serial_number_values.join(', ')}`);
        if (item.hsn_code) detailParts.push(`HSN: ${item.hsn_code}`);
        if (item.mrp && item.mrp > 0 && billSettings?.a4_show_mrp !== false) detailParts.push(`MRP: ${item.mrp.toFixed(2)}`);
        if (item.tax_percent) detailParts.push(`GST: ${item.tax_percent}%`);
        if (detailParts.length > 0) descParts.push(detailParts.join(' | '));
        
        return [
          index + 1,
          descParts.join('\n'),
          `${item.quantity}${item.stock_quantity != null ? ` (${item.stock_quantity} ${item.stock_unit || 'pcs'})` : ''}`,
          item.unit || 'PCS',
          (item.price || 0).toFixed(2),
          itemDiscount > 0 ? `${discountPercent.toFixed(1)}% / ${itemDiscount.toFixed(2)}` : '-',
          taxableAmount.toFixed(2)
        ];
      });
      
      autoTable(pdf, {
        startY: y,
        head: [[
          { content: 'S.No', styles: { halign: 'center' } },
          'Product Description',
          { content: 'Qty', styles: { halign: 'center' } },
          { content: 'Unit', styles: { halign: 'center' } },
          { content: 'Rate', styles: { halign: 'right' } },
          { content: 'Discount', styles: { halign: 'right' } },
          { content: 'Taxable Amt', styles: { halign: 'right' } }
        ]],
        body: tableData,
        theme: 'grid',
        headStyles: {
          fillColor: [245, 245, 245],
          textColor: black,
          fontStyle: 'bold',
          fontSize: 10,
          lineWidth: 0.3,
          lineColor: lineGray
        },
        bodyStyles: {
          fontSize: 10,
          textColor: darkGray,
          lineWidth: 0.3,
          lineColor: lineGray,
          cellPadding: 2.5
        },
        columnStyles: {
          0: { cellWidth: 12, halign: 'center' },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 14, halign: 'center' },
          3: { cellWidth: 14, halign: 'center' },
          4: { cellWidth: 24, halign: 'right' },
          5: { cellWidth: 28, halign: 'right' },
          6: { cellWidth: 28, halign: 'right' }
        },
        margin: { left: margin, right: margin },
        styles: { lineWidth: 0.3, lineColor: lineGray },
        didParseCell: autoTableMultilingualHook
      });
      
      y = (pdf.lastAutoTable?.finalY || y) + 5;
      
      // ---- GST SUMMARY + TOTALS SIDE BY SIDE ----
      const taxGroups = {};
      items.forEach(item => {
        const rate = item.tax_percent || 0;
        const baseAmount = item.quantity * item.price;
        const itemDiscount = item.item_discount || 0;
        const afterDiscount = baseAmount - itemDiscount;
        
        let taxableAmount, taxAmount;
        if (taxMethod === 'inclusive') {
          const taxDivisor = 1 + (rate / 100);
          taxableAmount = afterDiscount / taxDivisor;
          taxAmount = afterDiscount - taxableAmount;
        } else {
          taxableAmount = afterDiscount;
          taxAmount = (afterDiscount * rate) / 100;
        }
        
        if (!taxGroups[rate]) taxGroups[rate] = { taxable: 0, cgst: 0, sgst: 0, total: 0 };
        taxGroups[rate].taxable += taxableAmount;
        taxGroups[rate].cgst += taxAmount / 2;
        taxGroups[rate].sgst += taxAmount / 2;
        taxGroups[rate].total += taxableAmount + taxAmount;
      });
      
      const totalTaxable = Object.values(taxGroups).reduce((s, v) => s + v.taxable, 0);
      const totalCGST = Object.values(taxGroups).reduce((s, v) => s + v.cgst, 0);
      const totalSGST = Object.values(taxGroups).reduce((s, v) => s + v.sgst, 0);
      const totalGST = totalCGST + totalSGST;
      
      const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
      const totalItemDiscount = items.reduce((sum, item) => sum + (item.item_discount || 0), 0);
      const billDiscountAmt = bill.discount_amount || 0;
      
      const grandTotalRaw = taxMethod === 'inclusive' 
        ? (subtotal - totalItemDiscount - billDiscountAmt) 
        : (subtotal - totalItemDiscount - billDiscountAmt + totalGST);
      const roundedTotal = Math.round(grandTotalRaw);
      const roundOff = roundedTotal - grandTotalRaw;
      
      // Check if we need a new page
      if (y + 60 > pageHeight - 55) {
        pdf.addPage();
        y = margin;
      }
      
      // --- LEFT: GST Summary Table ---
      const gstTableWidth = contentWidth * 0.52;
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(...black);
      pdf.text('GST Summary', margin, y + 4);
      
      const gstTableData = Object.entries(taxGroups).map(([rate, vals]) => [
        `${rate}%`, vals.taxable.toFixed(2), vals.cgst.toFixed(2), vals.sgst.toFixed(2), vals.total.toFixed(2)
      ]);
      gstTableData.push([
        { content: 'Total', styles: { fontStyle: 'bold' } },
        { content: totalTaxable.toFixed(2), styles: { fontStyle: 'bold' } },
        { content: totalCGST.toFixed(2), styles: { fontStyle: 'bold' } },
        { content: totalSGST.toFixed(2), styles: { fontStyle: 'bold' } },
        { content: (totalTaxable + totalGST).toFixed(2), styles: { fontStyle: 'bold' } }
      ]);
      
      const gstStartY = y + 7;
      autoTable(pdf, {
        startY: gstStartY,
        head: [[
          { content: 'GST %', styles: { halign: 'center' } },
          { content: 'Taxable', styles: { halign: 'right' } },
          { content: 'CGST', styles: { halign: 'right' } },
          { content: 'SGST', styles: { halign: 'right' } },
          { content: 'Total', styles: { halign: 'right' } }
        ]],
        body: gstTableData,
        theme: 'grid',
        headStyles: { fillColor: [245, 245, 245], textColor: black, fontStyle: 'bold', fontSize: 9, lineWidth: 0.3, lineColor: lineGray },
        bodyStyles: { fontSize: 9, textColor: darkGray, lineWidth: 0.3, lineColor: lineGray, cellPadding: 2 },
        columnStyles: { 0: { cellWidth: 16, halign: 'center' }, 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } },
        tableWidth: gstTableWidth,
        margin: { left: margin }
      });
      
      // --- RIGHT: Totals Summary ---
      const totalsX = margin + gstTableWidth + 6;
      const totalsValX = pageWidth - margin;
      let tY = gstStartY;
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(...black);
      pdf.text('Summary', totalsX, tY - 3);
      tY += 3;
      
      const drawTotalLine = (label, value, bold = false) => {
        pdf.setFont('helvetica', bold ? 'bold' : 'normal');
        pdf.setFontSize(bold ? 11 : 10);
        pdf.setTextColor(...(bold ? black : darkGray));
        pdf.text(label, totalsX, tY);
        pdf.text(value, totalsValX, tY, { align: 'right' });
        tY += 6;
      };
      
      drawTotalLine('Sub Total:', subtotal.toFixed(2));
      if (totalItemDiscount > 0) drawTotalLine('Item Discount:', '- ' + totalItemDiscount.toFixed(2));
      if (billDiscountAmt > 0) drawTotalLine('Bill Discount:', '- ' + billDiscountAmt.toFixed(2));
      drawTotalLine('CGST:', totalCGST.toFixed(2));
      drawTotalLine('SGST:', totalSGST.toFixed(2));
      if (Math.abs(roundOff) >= 0.01) drawTotalLine('Round Off:', (roundOff >= 0 ? '+' : '') + roundOff.toFixed(2));
      
      // NET TOTAL line separator
      pdf.setDrawColor(...black);
      pdf.setLineWidth(0.5);
      pdf.line(totalsX, tY - 2, totalsValX, tY - 2);
      tY += 3;
      
      // NET TOTAL - use Rs. instead of ₹ to avoid encoding issues
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(...black);
      pdf.text('NET TOTAL:', totalsX, tY);
      pdf.text('Rs. ' + roundedTotal.toFixed(2), totalsValX, tY, { align: 'right' });
      tY += 8;
      
      y = Math.max((pdf.lastAutoTable?.finalY || y) + 5, tY + 2);
      
      // ---- AMOUNT IN WORDS ----
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(...black);
      pdf.text('Rupees:', margin, y);
      pdf.setFont('helvetica', 'normal');
      const wordsText = numberToWords(roundedTotal);
      const wordsLines = pdf.splitTextToSize(wordsText, contentWidth - 20);
      pdf.text(wordsLines, margin + 18, y);
      y += (wordsLines.length * 5) + 6;
      
      // ---- BOTTOM ROW: Bank Details | Terms | QR Code | Authorized Signatory ----
      if (y + 38 > pageHeight - 10) {
        pdf.addPage();
        y = margin;
      }
      
      // Separator
      pdf.setDrawColor(...lineGray);
      pdf.setLineWidth(0.3);
      pdf.line(margin, y, pageWidth - margin, y);
      y += 5;
      
      const hasBankDetails = businessInfo?.bank_account_number || businessInfo?.bank_name;
      const showQR = billSettings?.show_qr_code;
      const numCols = (hasBankDetails ? 1 : 0) + 1 + (showQR ? 1 : 0) + 1; // bank + terms + qr + signature
      const colW = contentWidth / numCols;
      
      let colX = margin;
      
      // Column: Bank Details
      if (hasBankDetails) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.setTextColor(...black);
        pdf.text('BANK DETAILS:', colX, y);
        
        let bY = y + 5;
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...darkGray);
        if (businessInfo?.bank_name) { pdf.text('Bank: ' + businessInfo.bank_name, colX, bY); bY += 4; }
        if (businessInfo?.bank_account_name) { pdf.text('A/C Name: ' + businessInfo.bank_account_name, colX, bY); bY += 4; }
        if (businessInfo?.bank_account_number) { pdf.text('A/C No: ' + businessInfo.bank_account_number, colX, bY); bY += 4; }
        if (businessInfo?.bank_branch_name) { pdf.text('Branch: ' + businessInfo.bank_branch_name, colX, bY); bY += 4; }
        if (businessInfo?.bank_ifsc_code) { pdf.text('IFSC: ' + businessInfo.bank_ifsc_code, colX, bY); }
        colX += colW;
      }
      
      // Column: Terms & Conditions
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(...black);
      pdf.text('Terms & Conditions:', colX, y);
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(...gray);
      const terms = billSettings?.terms_text || 'Goods once sold will not be exchanged or returned.';
      const termsLines = pdf.splitTextToSize(terms, colW - 5);
      pdf.text(termsLines, colX, y + 5);
      colX += colW;
      
      // Column: QR Code
      if (showQR) {
        let qrText = '';
        if (billSettings?.qr_code_type === 'url' && billSettings?.qr_code_url) {
          qrText = billSettings.qr_code_url;
        } else {
          // Bill details as plain text
          const itemsList = items.map(i => `${i.product_name} x${i.quantity} = Rs.${(i.quantity * i.price).toFixed(2)}`).join('\n');
          qrText = [
            `${isEstimation ? 'Estimation' : 'Invoice'}: ${bill.bill_number}`,
            `Date: ${formatDate(bill.created_at)}`,
            `Customer: ${custName}`,
            `---`,
            itemsList,
            `---`,
            `Total: Rs.${roundedTotal.toFixed(2)}`,
            `Tax: Rs.${totalGST.toFixed(2)} (CGST: ${totalCGST.toFixed(2)} + SGST: ${totalSGST.toFixed(2)})`,
            businessInfo?.business_name || ''
          ].join('\n');
        }
        
        try {
          const qrDataUrl = await QRCode.toDataURL(qrText, { width: 200, margin: 1, errorCorrectionLevel: 'M' });
          pdf.addImage(qrDataUrl, 'PNG', colX, y, 22, 22);
        } catch (qrErr) {
          pdf.setFontSize(7);
          pdf.setTextColor(...gray);
          pdf.text('QR Error', colX, y + 10);
        }
        colX += colW;
      }
      
      // Column: Authorized Signatory (right-aligned)
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(...black);
      pdf.text('For ' + (businessInfo?.business_name || 'Business'), pageWidth - margin, y, { align: 'right' });
      
      // Digital Signature Image (re-encoded via canvas so palette/transparent PNGs render correctly)
      const sigUrl = billSettings?.signature_url;
      if (sigUrl) {
        try {
          const sig = await loadImageForPdf(sigUrl);
          if (sig?.dataUrl) {
            const maxW = 38, maxH = 14;
            const ratio = sig.width / sig.height || 1;
            let w = maxW, h = maxW / ratio;
            if (h > maxH) { h = maxH; w = maxH * ratio; }
            pdf.addImage(sig.dataUrl, 'PNG', pageWidth - margin - w, y + 2, w, h);
          }
        } catch (e) { /* signature load failed, skip */ }
      }
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(...gray);
      pdf.text('Authorised Signatory', pageWidth - margin, y + 16, { align: 'right' });
      
      // ---- FOOTER ----
      pdf.setFontSize(7);
      pdf.setTextColor(...gray);
      pdf.text('This is a computer generated document.', pageWidth / 2, pageHeight - 5, { align: 'center' });
      
      // Save
      const fileName = `${isEstimation ? 'Estimation' : 'Invoice'}_${bill.bill_number}_${formatDate(bill.created_at).replace(/\//g, '-')}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating A4 invoice:', error);
      alert('Failed to generate A4 invoice. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // When autoPrint is enabled, render bill offscreen (hidden) — no modal shown to user
  if (autoPrint) {
    return (
      <div style={{ position: 'fixed', left: '-9999px', top: '-9999px', opacity: 0, pointerEvents: 'none', zIndex: -1 }}>
        <div style={{ width: '80mm', padding: '3mm', fontFamily: "'Courier New', monospace", backgroundColor: '#fff' }}>
          <div ref={printRef}>
            {/* Reuse same bill content structure for silent printing */}
            <div style={{ textAlign: 'center', fontSize: '11px', fontWeight: 'bold', padding: '4px 0', borderTop: '1px solid #000', borderBottom: '1px solid #000', margin: '0 0 4px 0', letterSpacing: '1px', color: '#000' }}>
              *** {documentTitle} ***
            </div>
            <div style={{ textAlign: 'center', marginBottom: '4px', color: '#000' }}>
              <div style={{ fontSize: '14px', fontWeight: 'bold', textTransform: 'uppercase', color: '#000' }}>
                {businessInfo?.business_name || 'Store Name'}
              </div>
              {billSettings?.show_address !== false && (
                <div style={{ fontSize: '9px', color: '#000', lineHeight: '1.3' }}>{getFullAddress()}</div>
              )}
              {billSettings?.show_contact_number !== false && businessInfo?.mobile && (
                <div style={{ fontSize: '9px', color: '#000' }}>Tel: {businessInfo.mobile}</div>
              )}
              {billSettings?.show_gst_number !== false && businessInfo?.gst_number && (
                <div style={{ fontSize: '9px', fontWeight: 'bold', color: '#000' }}>GSTIN: {businessInfo.gst_number}</div>
              )}
            </div>
            <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }}></div>
            <div style={{ fontSize: '9px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{bill.is_estimation ? 'Est No:' : 'Bill No:'}</span>
                <span style={{ fontWeight: 'bold' }}>{bill.bill_number || bill.estimation_number}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Date:</span>
                <span>{formatDate(bill.created_at)} {formatTime(bill.created_at)}</span>
              </div>
            </div>
            {bill.customer_name && bill.customer_name !== 'Guest' && (
              <>
                <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }}></div>
                <div style={{ fontSize: '9px' }}>
                  <div><strong>Customer:</strong> {bill.customer_name}</div>
                  {bill.customer_mobile && <div>Mobile: {bill.customer_mobile}</div>}
                </div>
              </>
            )}
            <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }}></div>
            <table style={{ width: '100%', fontSize: '9px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #000' }}>
                  <th style={{ textAlign: 'left', padding: '2px 0', fontWeight: 'bold' }}>Item</th>
                  <th style={{ textAlign: 'center', padding: '2px', fontWeight: 'bold' }}>Qty</th>
                  <th style={{ textAlign: 'right', padding: '2px', fontWeight: 'bold' }}>Rate</th>
                  <th style={{ textAlign: 'right', padding: '2px 0', fontWeight: 'bold' }}>Amt</th>
                </tr>
              </thead>
              <tbody>
                {(bill.items || []).map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px dotted #ccc' }}>
                    <td style={{ padding: '2px 0', fontWeight: 'bold' }}>
                      {idx + 1}. {item.product_name || item.name}
                      {item.serial_numbers?.length > 0 && (
                        <div style={{ fontSize: '7px', color: '#000' }}>S/N: {item.serial_numbers.join(', ')}</div>
                      )}
                      {item.hsn_code && <span style={{ fontSize: '7px', color: '#000' }}> HSN:{item.hsn_code}</span>}
                    </td>
                    <td style={{ textAlign: 'center', padding: '2px' }}>{item.quantity}{item.unit ? ` ${item.unit}` : ''}{item.stock_quantity != null ? ` (${item.stock_quantity} ${item.stock_unit || 'pcs'})` : ''}</td>
                    <td style={{ textAlign: 'right', padding: '2px' }}>{(item.price || item.selling_price || 0).toFixed(2)}</td>
                    <td style={{ textAlign: 'right', padding: '2px 0', fontWeight: 'bold' }}>{((item.quantity || 0) * (item.price || item.selling_price || 0)).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ borderTop: '1px solid #000', margin: '4px 0' }}></div>
            <div style={{ fontSize: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                <span>TOTAL</span>
                <span>Rs. {(bill.total || bill.grand_total || 0).toFixed(2)}</span>
              </div>
              {(bill.bill_discount_amount || bill.discount_amount || 0) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px' }}>
                  <span>Discount</span>
                  <span>-{(bill.bill_discount_amount || bill.discount_amount || 0).toFixed(2)}</span>
                </div>
              )}
              {bill.round_off !== undefined && bill.round_off !== 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px' }}>
                  <span>Round Off</span>
                  <span>{(bill.round_off || 0).toFixed(2)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '900', fontSize: '12px', borderTop: '1px solid #000', paddingTop: '2px' }}>
                <span>NET TOTAL</span>
                <span>Rs. {(bill.grand_total || bill.total || 0).toFixed(2)}</span>
              </div>
            </div>
            <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }}></div>
            <div style={{ textAlign: 'center', fontSize: '9px', fontWeight: 'bold', color: '#000' }}>
              {billSettings?.footer_text || 'Thank you for your purchase!'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-slate-50 to-slate-100 rounded-t-2xl">
          <div>
            <h3 className="text-lg font-bold text-slate-800">
              {bill.is_estimation ? 'Estimation' : 'Invoice'} Preview
            </h3>
            <p className="text-sm text-slate-500">{bill.bill_number || bill.estimation_number}</p>
          </div>
          <button 
            onClick={onClose} 
            className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-white rounded-full transition-all text-2xl"
          >
            ×
          </button>
        </div>

        {/* Print Preview */}
        <div className="flex-1 overflow-y-auto p-4 bg-slate-100">
          <div 
            className="bg-white mx-auto shadow-lg border border-slate-300" 
            style={{ width: '80mm', minHeight: '160mm', padding: '3mm', fontFamily: "'Courier New', monospace" }}
          >
            <div ref={printRef}>
              {/* Document Type Header */}
              <div className="center doc-type" style={{ textAlign: 'center', fontSize: '11px', fontWeight: 'bold', padding: '4px 0', borderTop: '1px solid #000', borderBottom: '1px solid #000', margin: '0 0 4px 0', letterSpacing: '1px', color: '#000' }}>
                *** {documentTitle} ***
              </div>

              {/* Business Header */}
              <div style={{ textAlign: 'center', marginBottom: '4px', color: '#000' }}>
                <div style={{ fontSize: '14px', fontWeight: 'bold', textTransform: 'uppercase', color: '#000' }}>
                  {businessInfo?.business_name || 'Store Name'}
                </div>
                {billSettings?.show_address !== false && (
                  <div style={{ fontSize: '9px', color: '#000', lineHeight: '1.3' }}>{getFullAddress()}</div>
                )}
                {billSettings?.show_contact_number !== false && businessInfo?.mobile && (
                  <div style={{ fontSize: '9px', color: '#000' }}>Tel: {businessInfo.mobile}</div>
                )}
                {billSettings?.show_gst_number !== false && businessInfo?.gst_number && (
                  <div style={{ fontSize: '9px', fontWeight: 'bold', color: '#000' }}>GSTIN: {businessInfo.gst_number}</div>
                )}
              </div>

              {/* Separator */}
              <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }}></div>

              {/* Bill Info */}
              <div style={{ fontSize: '9px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{bill.is_estimation ? 'Est No:' : 'Bill No:'}</span>
                  <span style={{ fontWeight: 'bold' }}>{bill.bill_number || bill.estimation_number}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Date:</span>
                  <span style={{ fontWeight: 'bold' }}>{formatDate(bill.created_at)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Time:</span>
                  <span style={{ fontWeight: 'bold' }}>{formatTime(bill.created_at)}</span>
                </div>
              </div>

              {/* Customer Info (only if not guest) */}
              {showCustomer && (
                <>
                  <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }}></div>
                  <div style={{ fontSize: '9px' }}>
                    <div style={{ fontSize: '8px', color: '#666' }}>Bill To:</div>
                    <div style={{ fontWeight: 'bold' }}>{bill.customer_name}</div>
                    {bill.customer_mobile && (
                      <div>Ph: {bill.customer_mobile}</div>
                    )}
                  </div>
                </>
              )}

              {/* Items Header Separator */}
              <div style={{ borderTop: '2px solid #000', margin: '6px 0 2px 0' }}></div>

              {/* Items Table */}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #000' }}>
                    <td style={{ padding: '2px', width: '15px' }}>#</td>
                    <td style={{ padding: '2px' }}>Item</td>
                    <td style={{ padding: '2px', textAlign: 'center', width: '25px' }}>Qty</td>
                    <td style={{ padding: '2px', textAlign: 'right', width: '35px' }}>Rate</td>
                    <td style={{ padding: '2px', textAlign: 'right', width: '25px' }}>Tax%</td>
                    <td style={{ padding: '2px', textAlign: 'right', width: '45px' }}>Amt</td>
                  </tr>
                </thead>
                <tbody>
                  {bill.items?.map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px dotted #ccc' }}>
                      <td style={{ padding: '2px', fontSize: '8px', color: '#666' }}>{idx + 1}</td>
                      <td style={{ padding: '2px' }}>
                        <div style={{ maxWidth: '100px', wordWrap: 'break-word' }}>{item.product_name}</div>
                        {item.description && <div style={{ fontSize: '7px', color: '#444', fontStyle: 'italic' }}>{item.description}</div>}
                        {item.serial_number_values?.length > 0 && <div style={{ fontSize: '7px', color: '#e65100' }}>SN: {item.serial_number_values.join(', ')}</div>}
                        {(item.hsn_code || (item.mrp && billSettings?.show_mrp !== false)) && <div style={{ fontSize: '7px', color: '#666' }}>{item.hsn_code ? `HSN: ${item.hsn_code}` : ''}{item.hsn_code && item.mrp && billSettings?.show_mrp !== false ? ' | ' : ''}{item.mrp && billSettings?.show_mrp !== false ? `MRP: ${item.mrp}` : ''}</div>}
                      </td>
                      <td style={{ padding: '2px', textAlign: 'center' }}>{item.quantity}{item.stock_quantity != null ? ` (${item.stock_quantity} ${item.stock_unit || 'pcs'})` : ''}</td>
                      <td style={{ padding: '2px', textAlign: 'right' }}>{item.price?.toFixed(0)}</td>
                      <td style={{ padding: '2px', textAlign: 'right', fontSize: '8px' }}>{item.tax_percent || 0}%</td>
                      <td style={{ padding: '2px', textAlign: 'right' }}>{item.total?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Totals Separator */}
              <div style={{ borderTop: '2px solid #000', margin: '4px 0' }}></div>

              {/* Totals Section */}
              <div style={{ fontSize: '9px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Total Qty: {totalItems}</span>
                  <span></span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
                  <span>Subtotal</span>
                  <span style={{ fontWeight: 'bold' }}>{formatCurrency(bill.subtotal)}</span>
                </div>
                {bill.discount_amount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
                    <span>Discount{bill.discount_percent > 0 ? ` (${bill.discount_percent}%)` : ''}</span>
                    <span>-{formatCurrency(bill.discount_amount)}</span>
                  </div>
                )}
                {bill.tax_amount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
                    <span>Tax (GST){billSettings?.tax_method === 'inclusive' ? ' Incl.' : ''}</span>
                    <span>{formatCurrency(bill.tax_amount)}</span>
                  </div>
                )}
                
                {/* Grand Total */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  fontSize: '12px', 
                  fontWeight: 'bold', 
                  padding: '4px 0', 
                  marginTop: '2px',
                  borderTop: '2px solid #000',
                  borderBottom: '2px solid #000'
                }}>
                  <span>TOTAL</span>
                  <span>{formatCurrency(bill.total || bill.grand_total)}</span>
                </div>
              </div>

              {/* GST Summary */}
              {taxSummary.length > 0 && billSettings?.show_gst_breakdown !== false && (
                <>
                  <div style={{ marginTop: '6px', padding: '3px', border: '1px solid #999', fontSize: '8px' }}>
                    <div style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: '2px' }}>GST SUMMARY</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #999' }}>
                          <td style={{ padding: '1px 2px' }}>Rate</td>
                          <td style={{ padding: '1px 2px', textAlign: 'right' }}>Taxable</td>
                          <td style={{ padding: '1px 2px', textAlign: 'right' }}>CGST</td>
                          <td style={{ padding: '1px 2px', textAlign: 'right' }}>SGST</td>
                        </tr>
                      </thead>
                      <tbody>
                        {taxSummary.map((tax, idx) => (
                          <tr key={idx}>
                            <td style={{ padding: '1px 2px' }}>{tax.rate}%</td>
                            <td style={{ padding: '1px 2px', textAlign: 'right' }}>{tax.taxable.toFixed(2)}</td>
                            <td style={{ padding: '1px 2px', textAlign: 'right' }}>{tax.cgst.toFixed(2)}</td>
                            <td style={{ padding: '1px 2px', textAlign: 'right' }}>{tax.sgst.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* Payment Info (for actual bills only) */}
              {!bill.is_estimation && bill.payment_modes?.length > 0 && (
                <>
                  <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }}></div>
                  <div style={{ fontSize: '9px' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>PAYMENT:</div>
                    {bill.payment_modes.map((pm, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ textTransform: 'uppercase' }}>{pm.mode}</span>
                        <span>{formatCurrency(pm.amount)}</span>
                      </div>
                    ))}
                    {bill.change_amount > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                        <span>Change</span>
                        <span>{formatCurrency(bill.change_amount)}</span>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Bill Notes */}
              {bill.notes && (
                <>
                  <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }}></div>
                  <div style={{ fontSize: '9px', padding: '3px', border: '1px dashed #ccc' }}>
                    <span style={{ fontWeight: 'bold', fontSize: '8px' }}>Note: </span>
                    {bill.notes}
                  </div>
                </>
              )}

              {/* Footer */}
              <div style={{ borderTop: '1px dashed #000', margin: '6px 0 0 0', paddingTop: '4px', textAlign: 'center' }}>
                <div style={{ fontSize: '10px', fontWeight: 'bold' }}>
                  {billSettings?.footer_text || 'Thank you for your purchase!'}
                </div>
                {billSettings?.terms_text && (
                  <div style={{ fontSize: '7px', color: '#666', marginTop: '2px', lineHeight: '1.2' }}>
                    {billSettings.terms_text}
                  </div>
                )}
                <div style={{ fontSize: '7px', color: '#999', marginTop: '4px' }}>Powered by GPOS</div>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
          <div className={`grid grid-cols-2 ${billSettings?.enable_a4_invoice ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} gap-2`}>
            <button
              onClick={handlePrint}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-800 text-white rounded-xl hover:bg-slate-900 font-medium transition-all"
              data-testid="print-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print
            </button>
            <button
              onClick={handleDownloadPDF}
              disabled={isGeneratingPDF}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium transition-all disabled:opacity-50"
              data-testid="download-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              {isGeneratingPDF ? '...' : 'PDF'}
            </button>
            {billSettings?.enable_a4_invoice && (
              <button
                onClick={handleGenerateA4Invoice}
                disabled={isGeneratingPDF}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium transition-all disabled:opacity-50"
                data-testid="a4-invoice-btn"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {isGeneratingPDF ? '...' : 'A4 Invoice'}
              </button>
            )}
            <button
              onClick={handleShare}
              disabled={isGeneratingPDF}
              className={`flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl hover:shadow-lg font-medium transition-all disabled:opacity-50 ${billSettings?.enable_a4_invoice ? '' : 'sm:col-span-1 col-span-2'}`}
              data-testid="share-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              {isGeneratingPDF ? 'Preparing...' : 'Share'}
            </button>
          </div>
          <p className="text-center text-xs text-slate-500 mt-3">
            {billSettings?.enable_a4_invoice 
              ? 'Print thermal, download PDF, A4 Invoice, or Share via WhatsApp & more'
              : 'Share as PDF via WhatsApp, Telegram, Email & more'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ThermalBillPrint;
